import { el, mount, unmount } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import calendarActivityTypes from 'constants/calendar-activity-types';

import { stringToSVG } from 'utilities';

import MoreActionsMenu from 'generics/components/more-actions-menu';
import MoreActionsMenuButton from 'generics/components/more-actions-menu/button';

import deleteNodeIconString from 'assets/images/delete-node-icon.svg?raw';

import moreActionsIndicatorIconString from 'assets/images/more-actions-indicator-icon.svg?raw';

import {
  determineDeleteButtonLabel,
  determineEventNature
} from './utilities';

const deleteNodeIcon = stringToSVG(deleteNodeIconString);
const moreActionsIndicatorIcon = stringToSVG(moreActionsIndicatorIconString);

/**
 * 
 * This same code is copied to the following files:
 * 1. Conversation Settings Widget
 * 
 * Abstract out to generic component when possible.
 * 
 * */
class CalendarActivitySettingsWidget {

  constructor(configuration) {

    this.menuDisplayed = false;
    this.activityDetails = configuration.activityDetails;
    this.onDelete = configuration.onDelete;
    this.user = configuration.user;
    this.onUninviteAttendee = configuration.onUninviteAttendee;
    this.onUninviteAttendeeFromUpcomingMeetings = configuration.onUninviteAttendeeFromUpcomingMeetings;

    this.activity = null;
    this.eventNature = null;

    this.anchor = this.renderAnchor();
    this.menu = null;

    this.el = el('#calendar-activity-settings-widget-main', [
      this.anchor
    ]);

  }

  renderAnchor() {

    this.anchor = el('button#conversation-settings-anchor', {
      class: 'application-button round-button with-ellipse-svg',
      onclick: this.toggleMenu.bind(this)
    }, [moreActionsIndicatorIcon.cloneNode(true)]);

    return this.anchor;

  }

  renderMenuButtons() {

    const deleteButton = new MoreActionsMenuButton({
      icon: deleteNodeIcon,
      label: determineDeleteButtonLabel(this.eventNature),
      onClick: this.handleDeleteButtonClick.bind(this)
    });

    const buttons = [
      deleteButton
    ];

    return buttons;

  }

  renderMenu() {

    const menuConfiguration = {
      anchor: this.anchor,
      buttons: this.renderMenuButtons(),
      onClose: this.toggleMenu.bind(this),
      class: 'export-widget-menu',
      verticalOffsetFromAnchor: 5
    };

    this.menu = new MoreActionsMenu(menuConfiguration);

    return this.menu;

  }

  async unassignUpcomingTasks() {

    if (typeof this.onUnassignUpcomingTasks === 'function') {
      await this.onUnassignUpcomingTasks(this.activity.event._id, this.activity._id);
    } else {
      await serverInterface.startup.calendarActivities.tasks.unassignUpcomingTasks(this.activity.event._id);
    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Task activities are deleted',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async unassignTask() {

    if (typeof this.onUnassignTask === 'function') {
      await this.onUnassignTask(this.activity.event._id, this.activity._id);
    } else {
      await serverInterface.startup.calendarActivities.tasks.unassignTask(this.activity.event._id);
    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Task activity deleted',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async confirmAndUnassignTasks() {

    const confirmed = await application.prompts.confirm({
      header: 'Leave from all upcoming tasks?',
      description: 'This task is part of a recurring series. Do you want to leave from all upcoming tasks as well? This does not change past tasks in the same series.',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No, only this one!'
    });

    if (confirmed === true) {
      return this.unassignUpcomingTasks();
    } else {
      return this.unassignTask();
    }

  }

  async tryUnassignTasks() {

    if (this.eventNature.recurring === true) {
      return this.confirmAndUnassignTasks();
    } else {
      return this.unassignTask();
    }

  }

  async deleteUpcomingTasks() {

    await serverInterface.startup.calendarActivities.tasks.deleteAllTasks(this.activity.event._id);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'All upcoming tasks are deleted',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async deleteTask() {

    await serverInterface.startup.calendarActivities.tasks.deleteTask(this.activity.event._id);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Task activity deleted',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async confirmAndDeleteParentTask() {

    const confirmed = await application.prompts.confirm({
      header: 'Delete all tasks?',
      description: 'This task is a parent task. Do you want to delete all tasks in this series?',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    });

    if (confirmed === true) {
      return this.deleteUpcomingTasks();
    }

    return false;

  }

  async confirmAndDeleteUpcomingTasks() {

    const confirmed = await application.prompts.confirm({
      header: 'Delete all upcoming tasks?',
      description: 'This task is part of a recurring series. Do you want to delete all tasks as well? This does not change past tasks in the same series.',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No, only this one!'
    });

    if (confirmed === true) {
      return this.deleteUpcomingTasks();
    } else {
      return this.deleteTask();
    }

  }

  async confirmAndDeleteRecurringTasks() {

    if (this.eventNature.isParent === true) {
      return this.confirmAndDeleteParentTask();
    } else {
      return this.confirmAndDeleteUpcomingTasks();
    }

  }

  tryDeleteTasks() {

    if (this.eventNature.recurring === true) {
      return this.confirmAndDeleteRecurringTasks();
    } else {
      return this.deleteTask();
    }

  }

  handleDeleteTaskButtonClick() {

    if (this.eventNature.readOnly === true) {
      return this.tryUnassignTasks();
    } else {
      return this.tryDeleteTasks();
    }

  }

  async leaveUpcomingMeetings() {

    if (typeof this.onUninviteAttendeeFromUpcomingMeetings === 'function') {
      await this.onUninviteAttendeeFromUpcomingMeetings(this.activity.event._id, this.activity._id);
    } else {
      await serverInterface.startup.calendarActivities.meetings.uninviteAttendeeFromUpcomingMeetings(this.activity.event._id);
    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'All upcoming meetings are left',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async leaveMeeting() {

    if (typeof this.onUninviteAttendee === 'function') {
      await this.onUninviteAttendee(this.activity.event._id, this.activity._id);
    } else {
      await serverInterface.startup.calendarActivities.meetings.uninviteAttendee(this.activity.event._id);
    }

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Meeting is left',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async cancelAppointment() {

    await serverInterface.startup.calendarActivities.meetings.cancelAppointmentByMeetingId(this.activity.event._id);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Appointment is cancelled',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async confirmAndLeaveMeetings() {

    const confirmed = await application.prompts.confirm({
      header: 'Leave from all upcoming meetings?',
      description: 'This meeting is part of a recurring series. Do you want to leave from all upcoming meetings as well? This does not change past meetings in the same series.',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No, only this one!'
    });

    if (confirmed === true) {
      return this.leaveUpcomingMeetings();
    } else {
      return this.leaveMeeting();
    }

  }

  tryLeaveMeetings() {

    if (this.eventNature.recurring === true) {
      return this.confirmAndLeaveMeetings();
    } else if (this.eventNature.subType === 'APPOINTMENT') {
      return this.confirmAndCancelAppointment();
    } else {
      return this.leaveMeeting();
    }

  }

  async confirmAndCancelAppointment() {

    const confirmed = await application.prompts.confirm({
      header: 'Cancel this appointment?',
      description: 'This cancels the appointment for everyone and notifies all attendees. This cannot be undone.',
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'No'
    });

    if (confirmed === true) {
      return this.cancelAppointment();
    }

    return false;

  }

  async deleteMeetings() {

    await serverInterface.startup.calendarActivities.meetings.deleteUpcomingMeetingsById(this.activity.event._id);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'All upcoming meetings are deleted',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async deleteMeeting() {

    await serverInterface.startup.calendarActivities.meetings.deleteMeetingById(this.activity.event._id);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Meeting is deleted',
      seconds: 5,
      type: 'info'
    });

    return true;

  }

  async tryDeleteParentMeeting() {

    const confirmed = await application.prompts.confirm({
      header: 'Delete all meetings?',
      description: 'This meeting is a parent meeting. Do you want to delete all meetings in this series?',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    });

    if (confirmed === true) {
      return this.deleteMeetings();
    }

    return false;

  }

  async confirmAndDeleteMeetings() {

    const confirmed = await application.prompts.confirm({
      header: 'Delete all upcoming meetings?',
      description: 'This meeting is part of a recurring series. Do you want to delete all meetings as well? This does not change past meetings in the same series.',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No, only this one!'
    });

    if (confirmed === true) {
      return this.deleteMeetings();
    } else {
      return this.deleteMeeting();
    }

  }

  async confirmAndDeleteRecurringMeetings() {

    if (this.eventNature.isParent === true) {
      return this.tryDeleteParentMeeting();
    } else {
      return this.confirmAndDeleteMeetings();
    }

  }

  tryDeleteMeetings() {

    if (this.eventNature.recurring === true) {
      return this.confirmAndDeleteRecurringMeetings();
    } else {
      return this.deleteMeeting();
    }

  }

  handleDeleteMeetingButtonClick() {

    if (this.eventNature.readOnly === true) {
      return this.tryLeaveMeetings();
    } else {
      return this.tryDeleteMeetings();
    }

  }

  async handleDeleteButtonClick() {

    this.hideMenu();

    const eventNature = this.eventNature;

    let result = true;

    if (eventNature.type === calendarActivityTypes.TASK) {
      result = await this.handleDeleteTaskButtonClick();
    } else {
      result = await this.handleDeleteMeetingButtonClick();
    }

    this.onDelete(result, this.activity._id);

  }

  hideMenu() {

    unmount(document.getElementById('root-level-dynamic-elements-container'), this.menu);

  }

  showMenu() {

    mount(document.getElementById('root-level-dynamic-elements-container'), this.menu);

  }

  toggleMenu() {

    this.menuDisplayed = this.menuDisplayed === false;

    if (this.menuDisplayed === false) {
      this.hideMenu();
    } else {
      this.showMenu();
    }

  }

  update(activity) {

    this.activity = activity;
    this.eventNature = determineEventNature(this.activity, this.user);

    this.menu = this.renderMenu();
  }

}

export default CalendarActivitySettingsWidget;